import { useState } from 'react'
import { Stack, Text, IconButton, DetailsList, IColumn, SelectionMode, DetailsListLayoutMode } from '@fluentui/react'
import { TopQuestionItem } from '../../api/models'

type TopQuestionsTableProps = {
  data: TopQuestionItem[] | null
}

const cardStyle = {
  root: {
    padding: '24px',
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    flex: 1,
  },
}

const PAGE_SIZE = 10

const TopQuestionsTable = ({ data }: TopQuestionsTableProps) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [sortColumn, setSortColumn] = useState<string>('count')
  const [sortDescending, setSortDescending] = useState(true)

  if (!data) return null

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDescending(!sortDescending)
    } else {
      setSortColumn(column)
      setSortDescending(true)
    }
    setCurrentPage(1)
  }

  const sortedData = [...data].sort((a, b) => {
    const key = sortColumn as keyof TopQuestionItem
    const aVal = a[key]
    const bVal = b[key]
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDescending ? bVal - aVal : aVal - bVal
    }
    const aStr = String(aVal ?? '').toLowerCase()
    const bStr = String(bVal ?? '').toLowerCase()
    return sortDescending ? bStr.localeCompare(aStr) : aStr.localeCompare(bStr)
  })

  const totalPages = Math.ceil(sortedData.length / PAGE_SIZE)
  const startIdx = (currentPage - 1) * PAGE_SIZE
  const pagedItems = sortedData.slice(startIdx, startIdx + PAGE_SIZE)

  const columns: IColumn[] = [
    {
      key: 'rank',
      name: '#',
      fieldName: 'rank',
      minWidth: 35,
      maxWidth: 35,
      onRender: (_item: TopQuestionItem, index?: number) => (
        <Text styles={{ root: { fontWeight: 600, color: '#605e5c' } }}>{startIdx + (index ?? 0) + 1}</Text>
      ),
    },
    {
      key: 'question',
      name: 'Question',
      fieldName: 'question',
      minWidth: 200,
      maxWidth: 700,
      isMultiline: true,
      isSorted: sortColumn === 'question',
      isSortedDescending: sortColumn === 'question' && sortDescending,
      iconName: sortColumn !== 'question' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('question'),
    },
    {
      key: 'count',
      name: 'Times Asked',
      fieldName: 'count',
      minWidth: 80,
      maxWidth: 100,
      isSorted: sortColumn === 'count',
      isSortedDescending: sortColumn === 'count' && sortDescending,
      iconName: sortColumn !== 'count' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('count'),
      onRender: (item: TopQuestionItem) => (
        <Text styles={{ root: { fontWeight: 600, color: '#2B3A67' } }}>{item.count}</Text>
      ),
    },
    {
      key: 'users',
      name: 'Asked By',
      fieldName: 'users',
      minWidth: 250,
      isMultiline: true,
      isSorted: sortColumn === 'unique_users',
      isSortedDescending: sortColumn === 'unique_users' && sortDescending,
      iconName: sortColumn !== 'unique_users' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('unique_users'),
      onRender: (item: TopQuestionItem) => {
        if (!item.users || item.users.length === 0) return '-'
        return (
          <Stack tokens={{ childrenGap: 2 }}>
            {item.users.map((u, i) => (
              <Text key={i} variant="small" styles={{ root: { color: '#323130' } }}>
                {u.name} <span style={{ color: '#a19f9d' }}>({u.email})</span>
              </Text>
            ))}
          </Stack>
        )
      },
    },
  ]

  return (
    <Stack styles={cardStyle} tokens={{ childrenGap: 12 }}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, color: '#605e5c' } }}>
          Most Repeated Questions
        </Text>
        <Text variant="small" styles={{ root: { color: '#a19f9d' } }}>
          {data.length > PAGE_SIZE
            ? `Showing ${startIdx + 1}-${Math.min(startIdx + PAGE_SIZE, data.length)} of ${data.length}`
            : `${data.length} question${data.length !== 1 ? 's' : ''} asked more than once`}
        </Text>
      </Stack>
      {data.length > 0 ? (
        <>
          <DetailsList
            items={pagedItems}
            columns={columns}
            selectionMode={SelectionMode.none}
            layoutMode={DetailsListLayoutMode.justified}
            isHeaderVisible={true}
            onShouldVirtualize={() => false}
          />
          <Stack
            horizontal
            horizontalAlign="center"
            verticalAlign="center"
            tokens={{ childrenGap: 8 }}
            styles={{ root: { paddingTop: 12 } }}
          >
            <IconButton
              iconProps={{ iconName: 'ChevronLeft' }}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              ariaLabel="Previous page"
            />
            <Text variant="medium">
              Page {currentPage} of {totalPages}
            </Text>
            <IconButton
              iconProps={{ iconName: 'ChevronRight' }}
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              ariaLabel="Next page"
            />
          </Stack>
        </>
      ) : (
        <Text styles={{ root: { color: '#a19f9d', textAlign: 'center', padding: 40 } }}>
          No repeated questions found for this period.
        </Text>
      )}
    </Stack>
  )
}

export default TopQuestionsTable
